import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import Card from 'apps/employer_renewals/components/card/Card'
import Header from 'apps/employer_renewals/components/Header'
import PageContainer from 'apps/employer_renewals/components/layout/PageContainer'
import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Banner, BorderedRadioButton, RadioGroup, TextButton } from 'slab'

import ExplanationModal from './ExplanationModal'

interface ALEStatusFormData {
  isALE: 'yes' | 'no'
}

export default function HeadcountPage () {
  const { navigateTo, shouldShowCensusPage } = useAppNavigation()
  const {
    employerIsALE,
    updateEmployersALEStatus,
    updateEmployerIsLoading,
    employerIsEstimatedToBeALE,
  } = useEmployer()
  const { employerRenewalConfig } = useEmployerRenewalConfig()

  const [explanationModalOpen, setExplanationModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const form = useForm<ALEStatusFormData>({ mode: 'onChange' })

  useEffect(() => {
    // employerIsEstimatedToBeALE being defined is a proxy for the employer data having successfully loaded
    if (employerIsEstimatedToBeALE !== undefined) {
      form.reset({
        isALE: employerIsALE === true
          ? 'yes'
          : employerIsALE === false
            ? 'no'
            : employerIsEstimatedToBeALE === true
              ? 'yes'
              : undefined
      })
    }
  }, [employerIsEstimatedToBeALE, form])

  const watchedALEStatus = form.watch('isALE')

  const onNextClick = async () => {
    if (!employerRenewalConfig || !watchedALEStatus) return
    setErrorMessage(null)

    try {
      await updateEmployersALEStatus(watchedALEStatus === 'yes')

      if (shouldShowCensusPage) {
        navigateTo(EmployerRenewalsRoute.EMPLOYEES_CENSUS)
      } else {
        navigateTo(EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY)
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error) {
        const apiError = error as GrapeAPIErrorType
        if (apiError.status === 422 && apiError.errors?.length) {
          setErrorMessage(`Unable to update headcount response: ${apiError.errors.join(', ')}`)
        } else {
          setErrorMessage(apiError.message || 'An error occurred, please try again or contact support if the problem persists.')
        }
      } else if (error instanceof Error) {
        setErrorMessage(`Error: ${error.message}`)
      } else {
        setErrorMessage('An error occurred, please try again or contact support if the problem persists.')
      }
    }
  }

  return (
    <PageContainer verticallyCentered>
      <Header heading='Tell us about your anticipated 2026 headcount' />
      <Card>
        <form>
          <div className='flex flex-col gap-4'>
            <Controller
              name='isALE'
              control={form.control}
              render={({ field }) => (
                <RadioGroup
                  name='isALE'
                  layout='horizontal'
                  label='Will you have over 50 full-time equivalent employees at any point in 2026?'
                  description={'If you\'re not sure or thinking "maybe", then select Yes'}
                  disabled={updateEmployerIsLoading || employerIsEstimatedToBeALE === undefined}
                >
                  <BorderedRadioButton
                    label='Yes'
                    value='yes'
                    size='small'
                    checked={field.value === 'yes'}
                    onChange={() => field.onChange('yes')}
                  />
                  <BorderedRadioButton
                    label='No'
                    value='no'
                    size='small'
                    checked={field.value === 'no'}
                    onChange={() => field.onChange('no')}
                  />
                </RadioGroup>
              )}
            />
            <div>
              <TextButton
                color='purple'
                size='small'
                onClick={() => { setExplanationModalOpen(true) }}
              >
                What qualifies as a full-time equivalent employee?
              </TextButton>
            </div>
          </div>
        </form>
      </Card>
      <ExplanationModal
        open={explanationModalOpen}
        onOpenChange={setExplanationModalOpen}
      />
      {errorMessage && (
        <Banner type='critical' title='Something went wrong'>
          {errorMessage}
        </Banner>
      )}
      <NavigationButtons
        isNextDisabled={!watchedALEStatus || employerRenewalConfig === undefined}
        isLoading={updateEmployerIsLoading}
        onNext={onNextClick}
      />
    </PageContainer>
  )
}
